import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Plus, Search, MapPin, Loader2, ChevronLeft, ChevronRight, ArrowLeft,
  Construction, Factory, Tent, Anchor, Building2, Home, MoreHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useSites, useCreateSite, useUpdateSite } from '@/hooks/useSites';
import { SiteForm } from './SiteForm';
import type { Site, SiteStatus, SiteType } from '@/lib/types';
import { useAuth } from '@/lib/auth';

type StatusFilter = 'all' | SiteStatus;

const SITE_TYPE_ICONS: Record<SiteType, typeof Construction> = {
  construction: Construction, industrial: Factory, camp: Tent, port: Anchor,
  commercial: Building2, residential: Home, other: MoreHorizontal,
};

export default function SitesPage() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const isRtl = i18n.language === 'ar' || i18n.language === 'ur';

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Site | null>(null);

  useEffect(() => {
    const tm = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(tm);
  }, [searchInput]);

  const params = useMemo(() => ({
    search: search || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    page, pageSize: 25,
  }), [search, statusFilter, page]);

  const { data, isLoading, isError, error, refetch } = useSites(params);
  const createMutation = useCreateSite();
  const updateMutation = useUpdateSite();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const handleCreate = async (input: Parameters<typeof createMutation.mutateAsync>[0]) => {
    await createMutation.mutateAsync(input);
    setCreating(false);
  };
  const handleUpdate = async (input: Parameters<typeof updateMutation.mutateAsync>[0]['input']) => {
    if (!editing) return;
    await updateMutation.mutateAsync({ id: editing.id, input });
    setEditing(null);
  };

  const STATUSES: StatusFilter[] = ['all', 'active', 'on_hold', 'completed', 'cancelled'];
  const displayName = user?.fullNameEn ?? user?.fullName ?? '';

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Logo size={36} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
              <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher /><ThemeToggle />
            <div className="ms-2 ps-2 border-s border-border">
              <Button variant="ghost" size="sm" onClick={logout}>{t('auth.logout')}</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2">
              <ArrowLeft className={`h-3 w-3 ${isRtl ? 'rotate-180' : ''}`} />{t('common.backToDashboard')}
            </Link>
            <div className="flex items-center gap-3">
              <MapPin className="h-7 w-7 text-gold" />
              <h1 className="font-display text-3xl text-foreground">{t('dashboard.modules.sites')}</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{t('sites.subtitle')}</p>
          </div>
          <Button variant="gold" size="lg" onClick={() => setCreating(true)} className="shrink-0">
            <Plus className="h-4 w-4" />{t('sites.addSite')}
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('sites.searchPlaceholder')} className="ps-10" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    statusFilter === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'
                  }`}>
                  {t(`sites.filter.${s}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="py-16 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : isError ? (
            <div className="py-16 text-center">
              <p className="text-destructive">{(error as Error)?.message ?? 'Error'}</p>
              <Button variant="outline" onClick={() => refetch()} className="mt-4">{t('common.retry')}</Button>
            </div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
              <h3 className="font-display text-xl text-foreground">
                {search || statusFilter !== 'all' ? t('sites.noResults') : t('sites.empty')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {search || statusFilter !== 'all' ? t('sites.tryDifferent') : t('sites.emptyDescription')}
              </p>
              {!search && statusFilter === 'all' && (
                <Button variant="gold" onClick={() => setCreating(true)} className="mt-6">
                  <Plus className="h-4 w-4" />{t('sites.addFirstSite')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {items.map((s) => {
                  const Icon = SITE_TYPE_ICONS[s.siteType];
                  const activeContracts = s.contracts ?? [];
                  return (
                    <button key={s.id} onClick={() => setEditing(s)}
                      className="text-start bg-background border border-border rounded-lg p-4 hover:border-gold/40 hover:shadow-md transition-all">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="bg-gold/10 p-2 rounded-md shrink-0">
                          <Icon className="h-5 w-5 text-gold" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-foreground truncate">{s.nameEn}</div>
                          {s.nameAr && <div className="text-xs text-muted-foreground truncate" dir="rtl">{s.nameAr}</div>}
                          <div className="text-[11px] font-mono text-muted-foreground mt-0.5">{s.code}</div>
                        </div>
                        <Badge variant={s.status === 'active' ? 'active' : s.status === 'on_hold' ? 'gold' : 'inactive'}>
                          {t(`sites.status.${s.status}`)}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3" />{s.city ?? '—'}{s.region ? `, ${s.region}` : ''}</div>
                        <div>{t(`sites.siteType.${s.siteType}`)}{s.legalEntity && ` · ${s.legalEntity.code}`}</div>
                        {activeContracts.length > 0 && (
                          <div className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-2 pt-2 border-t border-border">
                            {t('sites.activeContractsCount', { count: activeContracts.length })}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="px-4 py-3 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 bg-muted/20">
                <p className="text-sm text-muted-foreground">
                  {t('common.showingRange', { from: (page - 1) * 25 + 1, to: Math.min(page * 25, total), total })}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                  <span className="px-3 text-sm tabular-nums">{page} / {totalPages}</span>
                  <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                    <ChevronRight className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          {displayName} · {t(`dashboard.role.${user?.role ?? ''}`, { defaultValue: user?.role ?? '' })}
        </p>
      </main>

      <Sheet open={creating} onOpenChange={setCreating}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('sites.addSite')}</SheetTitle>
            <SheetDescription>{t('sites.addSiteDescription')}</SheetDescription>
          </SheetHeader>
          <SiteForm onSubmit={handleCreate} onCancel={() => setCreating(false)} submitLabel={t('sites.create')} />
        </SheetContent>
      </Sheet>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editing?.nameEn ?? ''}</SheetTitle>
            <SheetDescription className="font-mono text-xs">{editing?.code}</SheetDescription>
          </SheetHeader>
          <SiteForm initial={editing} contracts={editing?.contracts}
            onSubmit={handleUpdate} onCancel={() => setEditing(null)} submitLabel={t('common.save')} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
